/**
 * Render Queue Store
 *
 * Zustand store managing the per-project render queue.
 * Handles job CRUD, reordering, and serial runner coordination.
 *
 * Design: the store holds a flat array of RenderJobs. The queue runner
 * (a separate hook) reads from the store and drives execution serially
 * (WebGPU is a tab-wide singleton — concurrent renders would contend).
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { RenderJob, RenderJobStatus } from '../types/render-queue'

const isDev =
  typeof import.meta !== 'undefined' &&
  typeof import.meta.env !== 'undefined' &&
  !!import.meta.env.DEV

interface RenderQueueState {
  /** Ordered list of render jobs (front = next to render). */
  jobs: RenderJob[]
  /** Whether the queue runner is paused. */
  isPaused: boolean
  /** ID of the job currently being rendered, if any. */
  activeJobId: string | null
}

interface RenderQueueActions {
  // Job management
  enqueueJobs: (jobs: RenderJob[]) => void
  cancelJob: (jobId: string) => void
  retryJob: (jobId: string) => void
  removeJob: (jobId: string) => void
  clearCompleted: () => void
  clearAll: () => void

  // Reordering
  moveJobUp: (jobId: string) => void
  moveJobDown: (jobId: string) => void

  // Queue control
  pauseQueue: () => void
  resumeQueue: () => void

  // Runner-internal mutators
  markRendering: (jobId: string) => void
  updateJobProgress: (jobId: string, progress: number) => void
  markCompleted: (
    jobId: string,
    completion: { result: RenderJob['result']; savedPath?: string; fileSize?: number },
  ) => void
  markFailed: (jobId: string, error: string) => void
  markCancelled: (jobId: string) => void
  setActiveJob: (jobId: string | null) => void

  // Hydration
  hydrate: (state: { jobs: RenderJob[]; isPaused: boolean }) => void
  reset: () => void
}

export const useRenderQueueStore = create<RenderQueueState & RenderQueueActions>()(
  devtools(
    (set) => ({
      jobs: [],
      isPaused: false,
      activeJobId: null,

      /* ───────── Job management ───────── */

      enqueueJobs: (newJobs) =>
        set((s) => ({
          jobs: [...s.jobs, ...newJobs],
        })),

      cancelJob: (jobId) =>
        set((s) => ({
          jobs: s.jobs.map((j) =>
            j.id === jobId && j.status === 'queued'
              ? { ...j, status: 'cancelled' as RenderJobStatus }
              : j,
          ),
        })),

      retryJob: (jobId) =>
        set((s) => ({
          jobs: s.jobs.map((j) =>
            j.id === jobId
              ? { ...j, status: 'queued' as RenderJobStatus, progress: 0, result: undefined, error: undefined }
              : j,
          ),
        })),

      removeJob: (jobId) =>
        set((s) => ({
          jobs: s.jobs.filter((j) => j.id !== jobId),
          activeJobId: s.activeJobId === jobId ? null : s.activeJobId,
        })),

      clearCompleted: () =>
        set((s) => ({
          jobs: s.jobs.filter((j) => j.status !== 'completed' && j.status !== 'cancelled' && j.status !== 'failed'),
        })),

      clearAll: () =>
        set(() => ({
          jobs: [],
          activeJobId: null,
        })),

      /* ───────── Reordering ───────── */

      moveJobUp: (jobId) =>
        set((s) => {
          const idx = s.jobs.findIndex((j) => j.id === jobId)
          if (idx <= 0) return s
          const jobs = [...s.jobs]
          // Don't move a job before the currently rendering one
          const activeIdx = s.activeJobId ? s.jobs.findIndex((j) => j.id === s.activeJobId) : -1
          if (idx - 1 === activeIdx) return s
          ;[jobs[idx - 1]!, jobs[idx]!] = [jobs[idx]!, jobs[idx - 1]!]
          return { jobs }
        }),

      moveJobDown: (jobId) =>
        set((s) => {
          const idx = s.jobs.findIndex((j) => j.id === jobId)
          if (idx < 0 || idx >= s.jobs.length - 1) return s
          const jobs = [...s.jobs]
          ;[jobs[idx]!, jobs[idx + 1]!] = [jobs[idx + 1]!, jobs[idx]!]
          return { jobs }
        }),

      /* ───────── Queue control ───────── */

      pauseQueue: () => set({ isPaused: true }),
      resumeQueue: () => set({ isPaused: false }),

      /* ───────── Runner mutators ───────── */

      markRendering: (jobId) =>
        set((s) => ({
          activeJobId: jobId,
          jobs: s.jobs.map((j) =>
            j.id === jobId ? { ...j, status: 'rendering' as RenderJobStatus, progress: 0, error: undefined } : j,
          ),
        })),

      updateJobProgress: (jobId, progress) =>
        set((s) => ({
          jobs: s.jobs.map((j) => (j.id === jobId ? { ...j, progress } : j)),
        })),

      markCompleted: (jobId, completion) =>
        set((s) => ({
          activeJobId: null,
          jobs: s.jobs.map((j) =>
            j.id === jobId
              ? {
                  ...j,
                  status: 'completed' as RenderJobStatus,
                  progress: 100,
                  result: completion.result,
                  savedPath: completion.savedPath,
                  fileSize: completion.fileSize ?? completion.result?.fileSize,
                  error: undefined,
                }
              : j,
          ),
        })),

      markFailed: (jobId, error) =>
        set((s) => ({
          activeJobId: null,
          jobs: s.jobs.map((j) =>
            j.id === jobId ? { ...j, status: 'failed' as RenderJobStatus, error } : j,
          ),
        })),

      markCancelled: (jobId) =>
        set((s) => ({
          activeJobId: null,
          jobs: s.jobs.map((j) =>
            j.id === jobId ? { ...j, status: 'cancelled' as RenderJobStatus, progress: 0 } : j,
          ),
        })),

      setActiveJob: (jobId) => set({ activeJobId: jobId }),

      /* ───────── Hydration ───────── */

      hydrate: (state) =>
        set({
          jobs: state.jobs,
          isPaused: state.isPaused,
        }),

      reset: () =>
        set({
          jobs: [],
          isPaused: false,
          activeJobId: null,
        }),
    }),
    { name: 'RenderQueueStore', enabled: isDev },
  ),
)

/**
 * Get the next queued job that should be processed.
 */
export function getNextQueuedJob(jobs: RenderJob[]): RenderJob | undefined {
  return jobs.find((j) => j.status === 'queued')
}
